import { useRouter, useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";

import {
  Individual,
  MedicalLanding,
  MedicalLandingPage,
  OrganizerEdit,
  TravellerEdit,
} from "@/config/app-constant";
import { RootState } from "@/store/root-reducer";
import { getRouterPathBackButton } from "@/utils/page-routing.utils";

/**
 * Return type for useMedicalNavigation hook
 */
export interface UseMedicalNavigationReturn {
  handleBackButtonClick: () => void;
}

/**
 * Custom hook for medical section back navigation logic
 *
 * Encapsulates complex routing decisions based on:
 * - Edit mode state (organizerEdit, travellerEdit, or standard navigation)
 * - Organiser traveling status (attributes.organiser.travelling)
 * - Party type (attributes.tripDetails.partyType)
 *
 * @returns Object containing handleBackButtonClick function
 *
 * @example
 * ```tsx
 * const { handleBackButtonClick } = useMedicalNavigation();
 * <button onClick={handleBackButtonClick}>Back</button>
 * ```
 */
export default function useMedicalNavigation(): UseMedicalNavigationReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { attributes } = useSelector((state: RootState) => state.quote);

  const organizerEdit = searchParams.get(OrganizerEdit);
  const editTraveller = searchParams.get(TravellerEdit);

  const handleBackButtonClick = () => {
    // Determine if organiser is travelling and party type is Individual
    const isIndividualTravelling =
      attributes?.organiser?.travelling &&
      attributes?.tripDetails?.partyType === Individual;

    const targetPage = isIndividualTravelling
      ? MedicalLandingPage
      : MedicalLanding;

    let path = getRouterPathBackButton(targetPage);

    if (organizerEdit) {
      const editOrganiserPath = getRouterPathBackButton(targetPage);
      router.push(`${editOrganiserPath}/?${OrganizerEdit}=true`);
      return;
    }

    if (editTraveller) {
      const editTravellerPath = getRouterPathBackButton(targetPage);
      router.push(`${editTravellerPath}/?${TravellerEdit}=true`);
      return;
    }

    router.push(path);
  };

  return { handleBackButtonClick };
}
